import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiFilter,
  FiActivity,
  FiArrowUpCircle,
  FiArrowDownCircle,
  FiChevronDown,
  FiCreditCard,
  FiBriefcase,
  FiExternalLink,
  FiUsers,
  FiHeart
} from 'react-icons/fi';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Modal,
  PageComponent,
  Skeletons,
  Select,
  MultiSelect,
  DatePeriodFilter,
  getDateParams,
  formatDateToISO
} from '../../components';
import { BANK_OPTIONS } from '../../constants/banks';
import '../CompanyManager/style.css';
import './style.css';

function mergeIndicatorsList(indicatorList) {
  if (!Array.isArray(indicatorList) || indicatorList.length === 0) {
    return null;
  }

  const sections = ['revenue', 'expense'];
  const revenueKeys = ['total', 'overdue', 'dueToday', 'toReceive', 'received'];
  const expenseKeys = ['total', 'overdue', 'dueToday', 'toPay', 'paid'];

  const result = {
    revenue: {},
    expense: {}
  };

  sections.forEach(section => {
    const keys = section === 'revenue' ? revenueKeys : expenseKeys;
    keys.forEach(key => {
      const sum = indicatorList.reduce((acc, ind) => {
        const value = ind && ind[section] && ind[section][key];
        const num = Number(value || 0);
        if (Number.isNaN(num)) return acc;
        return acc + num;
      }, 0);
      result[section][key] = sum;
    });
  });

  return result;
}

function mergeChartDataList(chartDataLists) {
  const map = new Map();

  if (!Array.isArray(chartDataLists)) {
    return [];
  }

  chartDataLists.forEach(list => {
    if (!Array.isArray(list)) return;
    list.forEach(point => {
      if (!point || !point.date) return;
      const date = point.date;
      const existing = map.get(date) || { date, revenue: 0, expense: 0 };
      const revenue = Number(point.revenue || 0);
      const expense = Number(point.expense || 0);
      if (!Number.isNaN(revenue)) {
        existing.revenue += revenue;
      }
      if (!Number.isNaN(expense)) {
        existing.expense += expense;
      }
      map.set(date, existing);
    });
  });

  return Array.from(map.values()).sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return String(a.date).localeCompare(String(b.date));
  });
}

export default function FinancialOverview() {
  const permissions = usePermission('financial');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Dashboard Data
  const [indicators, setIndicators] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [transactions, setTransactions] = useState([]); // All fetched for calendar
  const [employeeCharges, setEmployeeCharges] = useState(null);
  const [showBenefitDetails, setShowBenefitDetails] = useState(false);
  const mergedBenefitBreakdown = useMemo(() => {
    const list = employeeCharges?.benefits?.breakdown;
    if (!Array.isArray(list)) return [];
    const map = new Map();
    list.forEach(item => {
      if (!item || !item.name) return;
      const key = item.name;
      const prev = map.get(key);
      const total = Number(item.total || 0);
      if (prev) {
        map.set(key, { ...prev, total: prev.total + total });
      } else {
        map.set(key, { ...item, total });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [employeeCharges?.benefits?.breakdown]);

  // Filters State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [periodMode, setPeriodMode] = useState('monthly');
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });
  
  const [company, setCompany] = useState({ id: 'all', trade_name: 'TODAS', name: 'TODAS' });
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedCostCenterId, setSelectedCostCenterId] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedPartnerType, setSelectedPartnerType] = useState('');

  // Options State
  const [companies, setCompanies] = useState([]);
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Calendar State
  const [calendarDate, setCalendarDate] = useState(new Date()); // View date for calendar
  const [selectedDay, setSelectedDay] = useState(new Date()); // Selected day for details
  
  // Details Filter State
  const [detailsFilter, setDetailsFilter] = useState('monthly');
  const [isDetailsFilterOpen, setIsDetailsFilterOpen] = useState(false);
  const detailsFilterRef = useRef(null);

  const { startDate: filterStartDate, endDate: filterEndDate } = useMemo(
    () => getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate),
    [periodMode, currentDate, customPeriod]
  );

  const workingDays = useMemo(() => {
    if (!filterStartDate || !filterEndDate) return 0;
    const start = new Date(filterStartDate); // Treat as UTC/Local consistently
    const end = new Date(filterEndDate);
    // Fix timezone offset issue if needed, but usually YYYY-MM-DD parsing as UTC is fine for day-of-week counting
    // Use simple iteration
    // To avoid timezone issues with new Date("YYYY-MM-DD"), let's append T00:00:00
    const startObj = new Date(filterStartDate + 'T00:00:00'); 
    const endObj = new Date(filterEndDate + 'T00:00:00');

    if (isNaN(startObj.getTime()) || isNaN(endObj.getTime()) || startObj > endObj) return 0;

    let count = 0;
    const cur = new Date(startObj);
    while (cur <= endObj) {
      const day = cur.getDay();
      if (day !== 0 && day !== 6) {
        count++;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  }, [filterStartDate, filterEndDate]);

  const loadFiltersData = useCallback(async (companyId) => {
    try {
      // Allow loading for 'all' as well
      const [
        customersRes,
        suppliersRes,
        employeesRes,
        categoriesRes,
        costCentersRes,
        accountsRes
      ] = await Promise.all([
        Api.getCustomers(companyId),
        Api.getSuppliers(companyId),
        Api.getEmployees(companyId),
        Api.getCategories(companyId),
        Api.getCostCenters(companyId),
        Api.getAccounts(companyId)
      ]);

      const partnerOptions = [
        ...(Array.isArray(customersRes) ? customersRes : []).map(c => ({ value: `customer-${c.id}`, label: `${c.name}` })),
        ...(Array.isArray(suppliersRes) ? suppliersRes : []).map(s => ({ value: `supplier-${s.id}`, label: `${s.name || s.trade_name}` })),
        ...(Array.isArray(employeesRes) ? employeesRes : []).map(e => ({ value: `employee-${e.id}`, label: `${e.name}` }))
      ];
      setPartners(partnerOptions);

      const categoryOptions = (Array.isArray(categoriesRes) ? categoriesRes : []).map(c => ({ value: c.id, label: c.name }));
      setCategories(categoryOptions);

      const costCenterOptions = (Array.isArray(costCentersRes) ? costCentersRes : []).map(cc => ({ value: cc.id, label: cc.name }));
      setCostCenters(costCenterOptions);

      const accOptions = Array.isArray(accountsRes) ? accountsRes.map(a => ({ value: a.id, label: a.name })) : [];
      setAccountOptions(accOptions);

    } catch (err) {
      console.error('Error loading filters', err);
    }
  }, []);

  const loadDashboardData = useCallback(async (companyId) => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
      const params = {};
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      if (selectedPartnerId) params.partnerId = selectedPartnerId;
      if (selectedPartnerType) params.partnerType = selectedPartnerType;
      if (selectedCategoryId) params.categoryId = selectedCategoryId;
      if (selectedCostCenterId) params.costCenterId = selectedCostCenterId;
      if (selectedAccountId) params.accountId = selectedAccountId;

      let dashboardRes, accountsRes;
      let chargesRes;

      if (companyId === 'all') {
        dashboardRes = await Api.getFinancialDashboard('all', params);
        accountsRes = await Api.getAccounts('all', params);
        chargesRes = await Api.getEmployeeCharges('all', { startDate, endDate });
      } else {
        [dashboardRes, accountsRes, chargesRes] = await Promise.all([
          Api.getFinancialDashboard(companyId, params),
          Api.getAccounts(companyId, params),
          Api.getEmployeeCharges(companyId, { startDate, endDate })
        ]);
      }
      
      if (!dashboardRes || !dashboardRes.indicators) {
        throw new Error('Falha ao carregar dados do dashboard.');
      }

      setIndicators(dashboardRes.indicators);
      setChartData(dashboardRes.chartData);
      setTransactions(dashboardRes.transactions || []);
      setAccounts(Array.isArray(accountsRes) ? accountsRes : []);
      setEmployeeCharges(chargesRes || null);
      setHasLoadedOnce(true);
      
    } catch (err) {
      setError('Erro ao carregar dashboard.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [periodMode, currentDate, selectedPartnerId, selectedPartnerType, selectedCategoryId, selectedCostCenterId, selectedAccountId, getDateParams]);

  const loadDashboardDataForSelection = useCallback(
    async (selectedCompanyIds) => {
      try {
        setLoading(true);
        const { startDate, endDate } = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
        const params = {};
        if (startDate && endDate) {
          params.startDate = startDate;
          params.endDate = endDate;
        }
        if (selectedPartnerId) params.partnerId = selectedPartnerId;
        if (selectedPartnerType) params.partnerType = selectedPartnerType;
        if (selectedCategoryId) params.categoryId = selectedCategoryId;
        if (selectedCostCenterId) params.costCenterId = selectedCostCenterId;
        if (selectedAccountId) params.accountId = selectedAccountId;

        const numericIds = Array.from(
          new Set(
            (selectedCompanyIds || [])
              .filter(v => v)
              .map(id => Number(id))
              .filter(id => !Number.isNaN(id))
          )
        );

        if (numericIds.length === 0) {
          setIndicators(null);
          setChartData([]);
          setTransactions([]);
          setAccounts([]);
          setHasLoadedOnce(true);
          setError('');
          return;
        }

        const dashboards = await Promise.all(
          numericIds.map(id =>
            Api.getFinancialDashboard(id, params).catch(() => null)
          )
        );

        const accountsLists = await Promise.all(
          numericIds.map(id =>
            Api.getAccounts(id, params).catch(() => [])
          )
        );
        const chargesLists = await Promise.all(
          numericIds.map(id =>
            Api.getEmployeeCharges(id, { startDate, endDate }).catch(() => null)
          )
        );

        const validDashboards = dashboards.filter(
          d => d && d.indicators
        );

        if (validDashboards.length === 0) {
          throw new Error('Falha ao carregar dados do dashboard.');
        }

        const mergedIndicators = mergeIndicatorsList(
          validDashboards.map(d => d.indicators)
        );

        const mergedChartData = mergeChartDataList(
          validDashboards.map(d =>
            Array.isArray(d.chartData) ? d.chartData : []
          )
        );

        const mergedTransactions = validDashboards.flatMap(d =>
          Array.isArray(d.transactions) ? d.transactions : []
        );

        const accountsMap = new Map();
        accountsLists.forEach((list, index) => {
          const companyId = numericIds[index];
          const companyObj = companies.find(c => c.id === companyId);
          const arr = Array.isArray(list) ? list : [];
          arr.forEach(acc => {
            if (!accountsMap.has(acc.id)) {
                accountsMap.set(acc.id, {
                  ...acc,
                  // If we want to show all companies, we might need to merge them if they come from different calls
                  // But usually the account object from backend already has 'companies' array if we requested it.
                  // For now, just ensure uniqueness.
                  company: companyObj || acc.company, // fallback
                  company_id: acc.company_id || companyId // fallback
                });
            }
          });
        });
        const mergedAccounts = Array.from(accountsMap.values());

        const validCharges = chargesLists.filter(c => c && c.salaries && c.benefits);
        let mergedCharges = null;
        if (validCharges.length > 0) {
          const salariesTotal = validCharges.reduce((acc, c) => acc + Number(c.salaries.total || 0), 0);
          const salariesPeriodTotal = validCharges.reduce(
            (acc, c) => acc + Number(c.salaries.periodTotal || 0),
            0
          );
          const breakdownMap = new Map();
          validCharges.forEach(c => {
            (Array.isArray(c.benefits.breakdown) ? c.benefits.breakdown : []).forEach(item => {
              const key = item.name;
              const prev = breakdownMap.get(key) || { name: item.name, total: 0 };
              breakdownMap.set(key, { name: item.name, total: prev.total + Number(item.total || 0) });
            });
          });
          const breakdown = Array.from(breakdownMap.values()).sort((a, b) => a.name.localeCompare(b.name));
          const benefitsTotal = breakdown.reduce((acc, b) => acc + Number(b.total || 0), 0);
          mergedCharges = {
            salaries: { total: salariesTotal, periodTotal: salariesPeriodTotal },
            benefits: {
              total: benefitsTotal,
              periodTotal: validCharges.reduce(
                (acc, c) => acc + Number(c.benefits.periodTotal || 0),
                0
              ),
              breakdown
            }
          };
        }

        setIndicators(mergedIndicators);
        setChartData(mergedChartData);
        setTransactions(mergedTransactions);
        setAccounts(mergedAccounts);
        setEmployeeCharges(mergedCharges);
        setHasLoadedOnce(true);
        setError('');
      } catch (err) {
        setError('Erro ao carregar dashboard.');
        console.error(err);
        setIndicators(null);
        setChartData([]);
        setTransactions([]);
        setAccounts([]);
        setEmployeeCharges(null);
      } finally {
        setLoading(false);
      }
    },
    [
      periodMode,
      currentDate,
      selectedPartnerId,
      selectedPartnerType,
      selectedCategoryId,
      selectedCostCenterId,
      selectedAccountId,
      getDateParams,
      companies
    ]
  );

  const loadCompanies = useCallback(async () => {
    try {
      const response = await Api.getCompanies();
      setCompanies(response);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  }, []);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      const selected = companies.find(c => String(c.id) === String(idsWithoutAll[0]));
      if (selected) {
        setCompany(selected);
        localStorage.setItem('selectedCompany', JSON.stringify(selected));
      }
    } else {
      const allCompany = { id: 'all', trade_name: 'TODAS', name: 'TODAS' };
      setCompany(allCompany);
      localStorage.setItem('selectedCompany', JSON.stringify(allCompany));
    }
  }

  // Close period menu on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (detailsFilterRef.current && !detailsFilterRef.current.contains(event.target)) {
        setIsDetailsFilterOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load Company and Filters
  useEffect(() => {
    loadCompanies();
    // Default to TODAS is handled by initial state
  }, [loadCompanies]);

  // Load Filters when company changes
  useEffect(() => {
    if (company) {
      loadFiltersData(company.id);
    }
  }, [company, loadFiltersData]);

  // Load Dashboard Data when filters or company selection change
  useEffect(() => {
    if (!permissions.canView) return;

    if (!companies || companies.length === 0) {
      return;
    }

    const allValue = 'all';
    const hasAll = companyFilterValues.includes(allValue);
    const specific = companyFilterValues.filter(v => v !== allValue);

    if (hasAll || specific.length === 0) {
      loadDashboardData('all');
    } else if (specific.length === 1) {
      loadDashboardData(specific[0]);
    } else {
      loadDashboardDataForSelection(specific);
    }

    setCalendarDate(new Date(currentDate));
    setSelectedDay(new Date(currentDate));
  }, [
    companies,
    companyFilterValues,
    currentDate,
    periodMode,
    selectedPartnerId,
    selectedCategoryId,
    selectedCostCenterId,
    selectedAccountId,
    loadDashboardData,
    loadDashboardDataForSelection
  ]);


  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };


  // Calendar Helpers
  const handleCalendarPrev = () => {
    setCalendarDate(prev => {
      const d = new Date(prev);
      d.setMonth(prev.getMonth() - 1);
      return d;
    });
  };

  const handleCalendarNext = () => {
    setCalendarDate(prev => {
      const d = new Date(prev);
      d.setMonth(prev.getMonth() + 1);
      return d;
    });
  };

  const calendarDays = useMemo(() => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startPadding = firstDay.getDay(); 
    for (let i = startPadding - 1; i >= 0; i--) {
        days.push({ date: new Date(year, month, -i), isCurrentMonth: false });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    let nextMonthDay = 1;
    while (days.length % 7 !== 0 || days.length < 35) {
        days.push({ date: new Date(year, month + 1, nextMonthDay++), isCurrentMonth: false });
    }
    return days;
  }, [calendarDate]);

  const getDayEvents = useCallback((date) => {
    const dateStr = formatDateToISO(date);
    return transactions.filter(t => (t.date === dateStr || t.due_date === dateStr));
  }, [transactions]);

  const filteredDetailEvents = useMemo(() => {
    const targetDate = selectedDay;
    const targetISO = formatDateToISO(targetDate);

    if (detailsFilter === 'daily') {
       return getDayEvents(targetDate);
    }

    return transactions.filter(t => {
      const tDateStr = t.date || t.due_date;
      if (!tDateStr) return false;
      
      const [tYear, tMonth, tDay] = tDateStr.split('-').map(Number);
      const checkDate = new Date(tYear, tMonth - 1, tDay); 

      const targetYear = targetDate.getFullYear();
      const targetMonth = targetDate.getMonth();
      
      switch (detailsFilter) {
        case 'weekly':
           const d = new Date(targetDate);
           const day = d.getDay(); 
           const diff = d.getDate() - day; 
           const startOfWeek = new Date(d.setDate(diff));
           startOfWeek.setHours(0,0,0,0);
           const endOfWeek = new Date(startOfWeek);
           endOfWeek.setDate(startOfWeek.getDate() + 6);
           endOfWeek.setHours(23,59,59,999);
           return checkDate >= startOfWeek && checkDate <= endOfWeek;
           
        case 'monthly':
           return tYear === targetYear && (tMonth - 1) === targetMonth;
           
        case 'annual':
           return tYear === targetYear;
           
        case 'last12':
           const oneYearAgo = new Date(targetDate);
           oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
           return checkDate >= oneYearAgo && checkDate <= targetDate;
           
        case 'all':
           return true;
           
        default:
           return tDateStr === targetISO;
      }
    });
  }, [detailsFilter, selectedDay, transactions, getDayEvents]);

  // Sort selected events: Commitments (no value/status?), Payables, Receivables
  // Actually transactions have types.
  const groupedSelectedEvents = useMemo(() => {
    const sortByDate = (a, b) => {
      const dateA = a.date || a.due_date || '';
      const dateB = b.date || b.due_date || '';
      return dateA.localeCompare(dateB);
    };

    const payables = filteredDetailEvents
      .filter(t => t.type === 'expense')
      .sort(sortByDate);

    const receivables = filteredDetailEvents
      .filter(t => t.type === 'revenue')
      .sort(sortByDate);

    const commitments = []; // If we had separate commitment type
    return { payables, receivables, commitments };
  }, [filteredDetailEvents]);

  const calendarMonthLabel = calendarDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
  const selectedDayLabel = selectedDay.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const getDetailsLabel = () => {
    if (detailsFilter === 'daily') return selectedDayLabel.charAt(0).toUpperCase() + selectedDayLabel.slice(1);
    if (detailsFilter === 'monthly') return selectedDay.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
    if (detailsFilter === 'annual') return selectedDay.getFullYear().toString();
    if (detailsFilter === 'weekly') return 'Esta Semana';
    if (detailsFilter === 'last12') return 'Últimos 12 Meses';
    if (detailsFilter === 'all') return 'Todo o Período';
    return selectedDayLabel;
  };

  function SkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiActivity size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '0.5rem' }}>
            {error}
          </div>
        )}
        {loading && !indicators ? <SkeletonWrapper /> : (
          <div className="financial-overview-container">
            {/* Header */}
            <div className="manager-header" style={{marginBottom: '10px'}}>
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiActivity size={22} />
                </div>
                <div>
                  <h1>Visão Geral do Financeiro</h1>
                  <p>Acompanhe o fluxo de caixa e indicadores financeiros.</p>
                </div>
              </div>
            </div>

            {/* Filters */}
        <Card className="manager-filters-card" style={{marginBottom: '0px'}}>
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
              </div>

              <div className="financial-filters-grid">
                {/* Period Filter */}
                <div className="filter-item-period">
                  <DatePeriodFilter
                    periodMode={periodMode}
                    setPeriodMode={(mode) => {
                      setPeriodMode(mode);
                      setDetailsFilter(mode);
                    }}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    customRange={customPeriod}
                    setCustomRange={setCustomPeriod}
                  />
                </div>

                <div className="filter-item-company">
                  <MultiSelect
                    id="companyFilter"
                    label="Empresa"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({ value: String(c.id), label: c.trade_name || c.name }))
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                    icon={FiBriefcase}
                  />
                </div>

                {/* Partner Type Filter */}
                <div className="filter-item-partner-type">
                  <Select
                    id="partnerTypeFilter"
                    label="Tipo Parceiro"
                    options={[
                      { value: '', label: 'TODOS' },
                      { value: 'customer', label: 'Cliente' },
                      { value: 'supplier', label: 'Fornecedor' },
                      { value: 'employee', label: 'Funcionário' }
                    ]}
                    value={selectedPartnerType}
                    onChange={(e) => {
                      setSelectedPartnerType(e.target.value);
                      setSelectedPartnerId('');
                    }}
                    searchable
                  />
                </div>

                {/* Partner Filter */}
                <div className="filter-item-partner">
                  <Select
                    id="partnerFilter"
                    label="Parceiro"
                    options={[{ value: '', label: 'TODOS' }, ...partners.filter(p => {
                      if (!selectedPartnerType) return true;
                      return p.value.startsWith(`${selectedPartnerType}-`);
                    })]}
                    value={selectedPartnerId}
                    onChange={(e) => setSelectedPartnerId(e.target.value)}
                    searchable
                  />
                </div>
                {/* Category Filter */}
                <div className="filter-item-category">
                  <Select
                    id="categoryFilter"
                    label="Categoria"
                    options={[{ value: '', label: 'TODAS' }, ...categories]}
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    searchable
                  />
                </div>
                {/* Cost Center Filter */}
                <div className="filter-item-cost-center">
                  <Select
                    id="costCenterFilter"
                    label="Centro de Custo"
                    options={[{ value: '', label: 'TODOS' }, ...costCenters]}
                    value={selectedCostCenterId}
                    onChange={(e) => setSelectedCostCenterId(e.target.value)}
                    searchable
                  />
                </div>
                {/* Account Filter */}
                <div className="filter-item-account">
                  <Select
                    id="accountFilter"
                    label="Conta Bancária"
                    options={[{ value: '', label: 'TODAS' }, ...accountOptions]}
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                    searchable
                  />
                </div>
              </div>
            </Card>

            {/* Indicators */}
            {indicators && (
              <div className="indicators-grid">
                <div className="indicator-card revenue">
                  <div className="indicator-header">
                    <div className="indicator-icon revenue">
                      <FiArrowUpCircle size={24} />
                    </div>
                    <div className="indicator-content">
                      <h3>Receitas</h3>
                      <div className="indicator-value">{formatCurrency(indicators?.revenue?.total ?? 0)}</div>
                      <div className="indicator-subtitle">Entradas confirmadas</div>
                    </div>
                  </div>
                  <div className="sub-indicators">
                    <div className="sub-indicator-pill">
                      <span className="sub-indicator-label text-red">Vencidos</span>
                      <span className="sub-indicator-amount text-red">{formatCurrency(indicators?.revenue?.overdue ?? 0)}</span>
                    </div>
                    <div className="sub-indicator-pill">
                      <span className="sub-indicator-label text-yellow">Vence Hoje</span>
                      <span className="sub-indicator-amount text-yellow">{formatCurrency(indicators?.revenue?.dueToday ?? 0)}</span>
                    </div>
                    <div className="sub-indicator-pill">
                      <span className="sub-indicator-label text-blue">A Vencer</span>
                      <span className="sub-indicator-amount text-blue">{formatCurrency(indicators?.revenue?.toReceive ?? 0)}</span>
                    </div>
                    <div className="sub-indicator-pill">
                      <span className="sub-indicator-label text-green">Recebidos</span>
                      <span className="sub-indicator-amount text-green">{formatCurrency(indicators?.revenue?.received ?? 0)}</span>
                    </div>
                  </div>
                </div>

                <div className="indicator-card expense">
                  <div className="indicator-header">
                    <div className="indicator-icon expense">
                      <FiArrowDownCircle size={24} />
                    </div>
                    <div className="indicator-content">
                      <h3>Despesas</h3>
                      <div className="indicator-value">{formatCurrency(indicators?.expense?.total ?? 0)}</div>
                      <div className="indicator-subtitle">Saídas registradas</div>
                    </div>
                  </div>
                  <div className="sub-indicators">
                    <div className="sub-indicator-pill">
                      <span className="sub-indicator-label text-red">Vencidos</span>
                      <span className="sub-indicator-amount text-red">{formatCurrency(indicators?.expense?.overdue ?? 0)}</span>
                    </div>
                    <div className="sub-indicator-pill">
                      <span className="sub-indicator-label text-yellow">Vence Hoje</span>
                      <span className="sub-indicator-amount text-yellow">{formatCurrency(indicators?.expense?.dueToday ?? 0)}</span>
                    </div>
                    <div className="sub-indicator-pill">
                      <span className="sub-indicator-label text-blue">A Vencer</span>
                      <span className="sub-indicator-amount text-blue">{formatCurrency(indicators?.expense?.toPay ?? 0)}</span>
                    </div>
                    <div className="sub-indicator-pill">
                      <span className="sub-indicator-label text-green">Pagos</span>
                      <span className="sub-indicator-amount text-green">{formatCurrency(indicators?.expense?.paid ?? 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {employeeCharges && employeeCharges.salaries && employeeCharges.benefits && (
              <div className="indicators-grid">
                <div className="indicator-card">
                  <div
                    className="indicator-header"
                    style={{ alignItems: 'flex-start' }}
                  >
                    <div
                      className="indicator-icon"
                      style={{ background: '#e0f2fe', color: '#0ea5e9' }}
                    >
                      <FiUsers size={24} />
                    </div>
                    <div className="indicator-content">
                      <h3>Encargos Salariais de Funcionários</h3>
                      <div className="indicator-value">
                        {formatCurrency(
                          (employeeCharges.salaries?.periodTotal ??
                            employeeCharges.salaries?.monthlyTotal ??
                            employeeCharges.salaries?.total ??
                            0)
                        )}
                      </div>
                      <div className="indicator-subtitle">
                        Custo bruto mensal:{' '}
                        {formatCurrency(
                          employeeCharges.salaries?.monthlyTotal ??
                            employeeCharges.salaries?.total ??
                            0
                        )}
                      </div>
                      <div className="indicator-subtitle">
                        Custo bruto anual:{' '}
                        {formatCurrency(
                          employeeCharges.salaries?.annualTotal ??
                            ((employeeCharges.salaries?.monthlyTotal ??
                              employeeCharges.salaries?.total ??
                              0) *
                              12)
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="indicator-card">
                  <div
                    className="indicator-header"
                    style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
                  >
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div
                        className="indicator-icon"
                        style={{ background: '#fde68a', color: '#d97706' }}
                      >
                        <FiHeart size={24} />
                      </div>
                      <div className="indicator-content">
                        <h3>Encargos de Benefícios de Funcionários</h3>
                        <div className="indicator-value">
                          {formatCurrency(
                            (employeeCharges.benefits?.periodTotal ??
                              employeeCharges.benefits?.monthlyTotal ??
                              employeeCharges.benefits?.total ??
                              0)
                          )}
                        </div>
                        <div className="indicator-subtitle">
                          Custo bruto mensal:{' '}
                          {formatCurrency(
                            (employeeCharges.benefits?.monthlyTotal ??
                              employeeCharges.benefits?.total ??
                              0) * workingDays
                          )}
                        </div>
                        <div className="indicator-subtitle">
                          Custo bruto anual:{' '}
                          {formatCurrency(
                            (employeeCharges.benefits?.annualTotal ??
                              ((employeeCharges.benefits?.monthlyTotal ??
                                employeeCharges.benefits?.total ??
                                0) *
                                12)) * workingDays
                          )}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => setShowBenefitDetails(true)}>
                      Ver detalhes
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <Modal
              isOpen={showBenefitDetails && !!employeeCharges}
              onClose={() => setShowBenefitDetails(false)}
              title="Detalhes dos benefícios de funcionários"
              size="md"
            >
              <div className="company-form-component">
                <div className="form-body">
                  <div className="form-grid">
                    <div className="form-group" style={{ gridColumn: '1 / -1', display: 'grid', gap: '8px' }}>
                      <div className="summary-card">
                        <div className="summary-label">Custo bruto mensal configurado de benefícios</div>
                        <div className="summary-value">
                          {formatCurrency(
                            (employeeCharges?.benefits?.monthlyTotal ??
                              employeeCharges?.benefits?.total ??
                              0) * workingDays
                          )}
                        </div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">Custo bruto anual configurado de benefícios</div>
                        <div className="summary-value">
                          {formatCurrency(
                            (employeeCharges?.benefits?.annualTotal ??
                              ((employeeCharges?.benefits?.monthlyTotal ??
                                employeeCharges?.benefits?.total ??
                                0) *
                                12)) * workingDays
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <div className="detail-list-header">
                        <span>Benefício</span>
                        <span>Valor Total</span>
                      </div>
                      <div className="detail-list" style={{ maxHeight: '260px' }}>
                        {mergedBenefitBreakdown.map(item => (
                          <div key={item.name} className="detail-list-row">
                            <span className="detail-list-name">{item.name}</span>
                            <span className="detail-list-amount">
                              {formatCurrency((item?.total ?? 0) * workingDays)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-actions">
                  <Button type="button" onClick={() => setShowBenefitDetails(false)}>
                    Fechar
                  </Button>
                </div>
              </div>
            </Modal>

            {/* Calendar */}
            <div className="calendar-container">
              <div className="calendar-main">
                <div className="calendar-header">
                  <button style={{display: 'none'}} className="calendar-nav-btn" onClick={handleCalendarPrev}>{'<< Ant.'}</button>
                  <span style={{ textTransform: 'capitalize' }}>{calendarMonthLabel}</span>
                  <button style={{display: 'none'}} className="calendar-nav-btn" onClick={handleCalendarNext}>{'Próx. >>'}</button>
                </div>
                <div className="calendar-grid">
                  {['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'].map(d => (
                    <div key={d} className="calendar-day-header">{d}</div>
                  ))}
                  {calendarDays.map((d, i) => {
                    const dayEvents = getDayEvents(d.date);
                    const hasRevenue = dayEvents.some(e => e.type === 'revenue');
                    const hasExpense = dayEvents.some(e => e.type === 'expense');
                    const isSelected = d.date.toDateString() === selectedDay.toDateString();
                    
                    return (
                      <div 
                        key={i} 
                        className={`calendar-day ${!d.isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedDay(d.date)}
                      >
                        <div className="calendar-day-number">{d.date.getDate()}</div>
                        <div className="calendar-day-dots">
                          {hasRevenue && <div className="calendar-dot revenue" />}
                          {hasExpense && <div className="calendar-dot expense" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="calendar-details">
                <div className="detail-header-date" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ textTransform: 'capitalize' }}>{getDetailsLabel()}</span>
                  
                  {/* Details Filter Dropdown */}
                  <div ref={detailsFilterRef} style={{ position: 'relative', display: 'none' }}>
                     <div 
                      onClick={() => setIsDetailsFilterOpen(!isDetailsFilterOpen)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.25rem', 
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: '#f3f4f6',
                        fontSize: '0.8rem',
                        fontWeight: 500
                      }}
                    >
                      <span style={{whiteSpace: 'nowrap'}}>
                        {detailsFilter === 'daily' ? 'Hoje' : 
                         detailsFilter === 'weekly' ? 'Semanal' :
                         detailsFilter === 'monthly' ? 'Mensal' :
                         detailsFilter === 'annual' ? 'Anual' :
                         detailsFilter === 'last12' ? 'Últimos 12 meses' : 'Todo o período'}
                      </span>
                      <FiChevronDown size={14} style={{ 
                        transform: isDetailsFilterOpen ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.2s'
                      }} />
                    </div>

                    {isDetailsFilterOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '0.25rem',
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        border: '1px solid var(--border-color)',
                        zIndex: 50,
                        minWidth: '140px',
                        overflow: 'hidden'
                      }}>
                        {[
                          { id: 'daily', label: 'Hoje' },
                          { id: 'weekly', label: 'Semanal' },
                          { id: 'monthly', label: 'Mensal' },
                          { id: 'annual', label: 'Anual' },
                          { id: 'last12', label: 'Últimos 12 meses' },
                          { id: 'all', label: 'Todo o período' }
                        ].map((option) => (
                          <div
                            key={option.id}
                            onClick={() => {
                              setDetailsFilter(option.id);
                              setIsDetailsFilterOpen(false);
                            }}
                            style={{
                              padding: '0.5rem 0.75rem',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              backgroundColor: detailsFilter === option.id ? '#f3f4f6' : 'transparent',
                              color: detailsFilter === option.id ? 'var(--primary-color)' : 'var(--text-primary)',
                              fontWeight: detailsFilter === option.id ? 500 : 400,
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (detailsFilter !== option.id) e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              if (detailsFilter !== option.id) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {option.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contas a Pagar */}
                <div className="detail-section">
                  <div className="detail-section-title payable">Contas a Pagar</div>
                  <div className="detail-list">
                    {groupedSelectedEvents.payables.length > 0 ? (
                      groupedSelectedEvents.payables.map(t => {
                        const tDateStr = t.date || t.due_date;
                        const dateDisplay = tDateStr ? tDateStr.split('-').reverse().slice(0, 2).join('/') : '-';
                        
                        return (
                          <div key={t.id} className="detail-item payable">
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                              <span style={{fontSize: '11px', color: '#6b7280', fontWeight: 500}}>{dateDisplay}</span>
                              <span>{t.description || (t.supplier && t.supplier.name) || 'Sem descrição'}</span>
                            </div>
                            <strong>{formatCurrency(t.value)}</strong>
                          </div>
                        );
                      })
                    ) : (
                      <div className="detail-empty">Não foram localizadas Contas a Pagar para esta data</div>
                    )}
                  </div>
                  {groupedSelectedEvents.payables.length > 0 && (
                     <div style={{textAlign: 'right', fontWeight: 'bold', fontSize: '13px', marginTop: '8px'}}>
                        Total: {formatCurrency(groupedSelectedEvents.payables.reduce((acc, t) => acc + Number(t.value), 0))}
                     </div>
                  )}
                </div>

                {/* Contas a Receber */}
                <div className="detail-section">
                  <div className="detail-section-title receivable">Contas a Receber</div>
                  <div className="detail-list">
                    {groupedSelectedEvents.receivables.length > 0 ? (
                      groupedSelectedEvents.receivables.map(t => {
                        const tDateStr = t.date || t.due_date;
                        const dateDisplay = tDateStr ? tDateStr.split('-').reverse().slice(0, 2).join('/') : '-';

                        return (
                          <div key={t.id} className="detail-item receivable">
                            <div style={{display: 'flex', flexDirection: 'column'}}>
                              <span style={{fontSize: '11px', color: '#6b7280', fontWeight: 500}}>{dateDisplay}</span>
                              <span>{t.description || (t.customer && t.customer.name) || 'Sem descrição'}</span>
                            </div>
                            <strong>{formatCurrency(t.value)}</strong>
                          </div>
                        );
                      })
                    ) : (
                      <div className="detail-empty">Não foram localizadas Contas a Receber para esta data</div>
                    )}
                  </div>
                   {groupedSelectedEvents.receivables.length > 0 && (
                     <div style={{textAlign: 'right', fontWeight: 'bold', fontSize: '13px', marginTop: '8px'}}>
                        Total: {formatCurrency(groupedSelectedEvents.receivables.reduce((acc, t) => acc + Number(t.value), 0))}
                     </div>
                  )}
                </div>
              </div>
            </div>

            {/* Chart and Bank Accounts */}
            <div className="bottom-grid">
              <Card className="chart-card">
                <h3 style={{marginTop: '0px'}}>Fluxo de Receitas e Despesas</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(date) => {
                         // date is YYYY-MM-DD
                         const parts = date.split('-');
                         return `${parts[2]}/${parts[1]}`;
                      }}
                    />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" />
                    <Tooltip 
                       formatter={(value) => formatCurrency(value)}
                       labelFormatter={(label) => new Date(label).toLocaleDateString('pt-BR')}
                    />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" name="Receitas" stroke="#10b981" fillOpacity={1} fill="url(#colorRevenue)" />
                    <Area type="monotone" dataKey="expense" name="Despesas" stroke="#ef4444" fillOpacity={1} fill="url(#colorExpense)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <Card className="bank-accounts-card">
                <div className="bank-accounts-header-container">
                  <div className="bank-accounts-title-group">
                    <h3 style={{marginTop: '0px', marginBottom: '0px'}}>Contas Bancárias</h3>
                    <button className="view-accounts-link" onClick={() => navigate('/financial/accounts')}>
                      Ver contas <FiExternalLink size={12} />
                    </button>
                  </div>
                  <div className="bank-accounts-total-wrapper">
                    <span className="total-label">Saldo Total</span>
                    <span className={`total-value ${accounts.reduce((sum, acc) => sum + Number(acc.balance || 0), 0) < 0 ? 'negative' : ''}`}>
                      {formatCurrency(accounts.reduce((sum, acc) => sum + Number(acc.balance || 0), 0))}
                    </span>
                  </div>
                </div>
                <div className="bank-accounts-list">
                  {accounts.length > 0 ? (
                      accounts.map(acc => (
                          <div key={acc.id} className="bank-account-item">
                              <div className="bank-account-main-row">
                                <div className="bank-account-left">
                                  <div className="bank-account-icon-wrapper">
                                    {acc.type === 'cash' ? <FiBriefcase size={20} /> : <FiCreditCard size={20} />}
                                  </div>
                                  <div className="bank-account-info">
                                      <span className="bank-account-name">{acc.name}</span>
                                      <span className="bank-account-bank">
                                        {acc.type === 'cash' 
                                          ? 'Caixa Físico' 
                                          : (BANK_OPTIONS.find(b => b.value === acc.bank)?.label || acc.bank || 'Banco não informado')}
                                      </span>
                                      {(acc.companies?.length > 0 || acc.company) && (
                                        <span className="bank-account-company" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                                            {acc.companies?.length > 0 
                                              ? acc.companies.map(c => c.trade_name || c.corporate_name).join(', ')
                                              : (acc.company?.trade_name || acc.company?.corporate_name)}
                                        </span>
                                      )}
                                  </div>
                                </div>
                                <span className={`bank-account-balance ${Number(acc.balance) < 0 ? 'negative' : ''}`}>
                                    {formatCurrency(acc.balance)}
                                </span>
                              </div>
                              <div className="bank-account-indicators">
                                <div className="account-indicator revenue">
                                  <FiArrowUpCircle size={12} />
                                  <span>{formatCurrency(acc.period_revenue || 0)}</span>
                                </div>
                                <div className="account-indicator expense">
                                  <FiArrowDownCircle size={12} />
                                  <span>{formatCurrency(acc.period_expense || 0)}</span>
                                </div>
                              </div>
                          </div>
                      ))
                  ) : (
                      <div className="detail-empty">Nenhuma conta bancária cadastrada.</div>
                  )}
                </div>
              </Card>
            </div>

          </div>
        )}
      </div>
    </PageComponent>
  );
}
